import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { PRICING_DISPLAY, type PricingTierId } from '@/lib/waitlist';

export interface PricingCardProps {
  tier: PricingTierId;
  /** Marks this card as the user's current plan (Settings > Usage tab). */
  current?: boolean;
  /** Visually highlights the recommended tier. */
  highlight?: boolean;
  /** Click handler — for Checkout / portal / sign-up navigation. */
  onCta?: () => void;
  /** When set, the CTA renders as an `<a href>` instead of a button (e.g. mailto for Custom). */
  ctaHref?: string;
  /** Disable the CTA (e.g. while submitting elsewhere). */
  disabled?: boolean;
  /** Show a spinner on the CTA (Checkout / portal redirect in flight). */
  loading?: boolean;
  /**
   * Label used when `current` is true. Defaults to "Your current plan" — the
   * caller overrides this to "Manage billing" once a Stripe customer exists.
   */
  currentCtaLabel?: string;
  /**
   * Explicit override for the CTA label. Wins over both `currentCtaLabel` and
   * the plan's default `plan.cta`. Useful for context-specific labels like
   * "Switch to Free" on the Free card when the user is on a paid tier.
   */
  ctaLabel?: string;
}

export function PricingCard({
  tier,
  current = false,
  highlight = false,
  onCta,
  ctaHref,
  disabled = false,
  loading = false,
  currentCtaLabel = 'Your current plan',
  ctaLabel,
}: PricingCardProps) {
  const plan = PRICING_DISPLAY[tier];
  const isFree = tier === 'free';
  const isCustom = tier === 'custom';

  // Custom tier shows "Custom" instead of a dollar figure; Free shows "Free".
  // Everything else: "$29" + "/ month".
  const priceDisplay = isFree ? 'Free' : isCustom ? 'Custom' : `$${plan.price}`;
  const showInterval = !isFree && !isCustom && plan.price !== null;

  const resolvedLabel = ctaLabel ?? (current ? currentCtaLabel : plan.cta);
  // Free + current → disabled (no action). Paid + current → enabled if caller
  // wired up a Manage-billing handler (currentCtaLabel was overridden).
  const ctaDisabled =
    disabled ||
    loading ||
    (current && (isFree || currentCtaLabel === 'Your current plan'));

  // Button styling per tier role:
  //   - Recommended → solid primary (default)
  //   - Free        → solid dark (high-contrast "Start free" CTA on white card)
  //   - Custom      → outline (de-emphasized — sales conversation, not core menu)
  //   - Scale/other → outline
  const variant = highlight ? 'default' : isFree ? 'secondary' : 'outline';
  const buttonClass = cn(
    'w-full',
    isFree && !highlight && 'bg-foreground text-background hover:bg-foreground/90',
  );

  return (
    <Card
      className={cn(
        'relative flex h-full flex-col p-7 transition',
        // Both `current` and `highlight` get the primary border treatment.
        // The badge below disambiguates which is which.
        current || highlight
          ? 'border-primary shadow-[0_8px_30px_-12px_rgba(255,84,54,0.35)]'
          : 'border-border',
      )}
    >
      {current ? (
        <span className="bg-primary text-primary-foreground absolute -top-3 left-7 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em]">
          Current plan
        </span>
      ) : highlight ? (
        <span className="bg-primary text-primary-foreground absolute -top-3 left-7 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em]">
          Recommended
        </span>
      ) : null}

      <div>
        <h3 className="font-display text-xl font-semibold tracking-tight">{plan.name}</h3>
        {/* min-h reserves 3 lines so the price row below stays vertically aligned
            across cards even when descriptions wrap to different lengths. */}
        <p className="text-muted-foreground mt-1 min-h-[4.25rem] text-sm leading-relaxed">
          {plan.description}
        </p>
      </div>

      <div className="mt-6 flex items-baseline gap-1">
        <span className="font-display text-4xl font-semibold tracking-tight">
          {priceDisplay}
        </span>
        {showInterval ? (
          <span className="text-muted-foreground text-sm">/ month</span>
        ) : null}
      </div>

      <ul className="mt-6 grid gap-2.5 text-sm">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <span className="bg-success mt-[5px] inline-block size-1.5 shrink-0 rounded-full" />
            <span className="text-foreground/90">{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-7">
        {ctaHref ? (
          <Button asChild variant={variant} className={buttonClass}>
            <a href={ctaHref}>
              {loading ? <Loader2 className="size-4 animate-spin" /> : null}
              {resolvedLabel}
            </a>
          </Button>
        ) : (
          <Button
            type="button"
            onClick={onCta}
            disabled={ctaDisabled}
            variant={variant}
            className={buttonClass}
          >
            {loading ? <Loader2 className="size-4 animate-spin" /> : null}
            {resolvedLabel}
          </Button>
        )}
      </div>
    </Card>
  );
}
